import { Suspense, useCallback, useMemo } from 'react'

import { Link, matchPath } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { useEventModuleAvailability } from '@tribeplatform/react-components/Event/hooks'
import { useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useRouterPath } from '@tribeplatform/react-sdk'
import { BackgroundProvider } from '@tribeplatform/react-ui-kit/BackgroundContext'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { NavVertical, Sidebar } from '@tribeplatform/react-ui-kit/Sidebar'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { ErrorBoundary } from '../../Error/ErrorBoundry.js'

import { AdminProfileMenu } from './AdminProfileMenu.js'
import { MainSidebarContent } from './MainSidebarContent.js'
import { NestedSidebarContent } from './NestedSidebarContent.js'
import {
  SidebarItem,
  sidebarsFactory,
  virtualSidebarRootFactory,
} from './sidebars.js'

const getActiveItem = (
  pathname: string,
  sidebars: ReturnType<typeof sidebarsFactory>,
) => {
  return sidebars.find(item => {
    const matched = !!matchPath(
      {
        path: item.path,
        end: item.exact || false,
        caseSensitive: item.exact || false,
      },
      pathname,
    )
    if (matched) {
      return true
    }

    return !!item?.children?.find(
      child =>
        !!matchPath(
          {
            path: child.path,
            end: child.exact || false,
            caseSensitive: item.exact || false,
          },
          pathname,
        ),
    )
  })
}

interface ActiveItemSidebarContentProps {
  activeItem: SidebarItem
  onSelect?: (item: SidebarItem) => void
}

const ActiveItemSidebarContent = ({
  activeItem,
  onSelect,
}: ActiveItemSidebarContentProps) => {
  const activeItemChildren = activeItem?.children?.length ? (
    <MainSidebarContent
      key={activeItem?.key ?? activeItem?.path}
      sidebar={activeItem}
      onSelect={onSelect}
    />
  ) : null

  const activeItemComponent = activeItem?.component ?? activeItemChildren

  if (!activeItemComponent) {
    return null
  }

  return (
    <Sidebar size="lg" className="h-full flex-grow">
      <Sidebar.Content className="pb-3">{activeItemComponent}</Sidebar.Content>
    </Sidebar>
  )
}

export interface AdminSidebarProps {
  onSelect?: (item: SidebarItem) => void
}

export const AdminSidebar = ({ onSelect = undefined }: AdminSidebarProps) => {
  const { isEventModuleEnabledOnNetwork } = useEventModuleAvailability()
  const sidebars = useStaticIntl(sidebarsFactory, isEventModuleEnabledOnNetwork)
  const virtualSidebarRoot = useStaticIntl(
    virtualSidebarRootFactory,
    isEventModuleEnabledOnNetwork,
  )
  const pathname = useRouterPath()

  const activeItem = useMemo(
    () => getActiveItem(pathname, sidebars),
    [pathname, sidebars],
  )

  const handlePrimaryNavClick = useCallback(
    (e: React.MouseEvent, item: SidebarItem) => {
      if (activeItem?.path === item.path) {
        e.preventDefault()
        return
      }
      const hasSecondarySidebar = !!item.children?.length || !!item.component
      if (!hasSecondarySidebar) {
        onSelect?.(item)
      }
    },
    [activeItem, onSelect],
  )

  // TODO: we can remove this
  const isNestedSidebar = [
    `${adminRoutes.customizer.templates.root}/*`,
    `${adminRoutes.design.appearance.root}/*`,
    adminRoutes.content.cms.root,
    `${adminRoutes.content.cms.root}/:slug`,
  ].some(path => matchPath({ path }, pathname))

  return (
    <div className="flex size-full">
      <BackgroundProvider backgroundType="surface">
        <NavVertical
          size="lg"
          collapsed
          className="p-3 h-full overflow-auto bg-surface border-line-subdued border-r justify-between"
        >
          <NavVertical.Group>
            {sidebars.map(item => {
              const active = activeItem?.path === item.path
              const navItem = (
                <NavVertical.Item
                  key={item?.key ?? item.path}
                  active={active}
                  as={Link}
                  leadingIcon={
                    typeof item.icon === 'string' ? (
                      <SvgIcon name={item.icon} size="1.5x" />
                    ) : (
                      item.icon
                    )
                  }
                  onClick={e => handlePrimaryNavClick(e, item)}
                  to={item.href ?? item.path}
                />
              )
              if (active) {
                return navItem
              }
              return (
                <Tooltip key={item?.key ?? item.path} placement="right">
                  <Tooltip.Trigger>{navItem}</Tooltip.Trigger>
                  <Tooltip.Panel>{item.title}</Tooltip.Panel>
                </Tooltip>
              )
            })}
          </NavVertical.Group>
          <NavVertical.Group>
            <AdminProfileMenu />
          </NavVertical.Group>
        </NavVertical>
      </BackgroundProvider>

      {isNestedSidebar && (
        <Sidebar size="lg" className="h-full flex-grow">
          <Sidebar.Content>
            <ErrorBoundary>
              <NestedSidebarContent sidebar={virtualSidebarRoot} />
            </ErrorBoundary>
          </Sidebar.Content>
        </Sidebar>
      )}
      {!isNestedSidebar && (
        <Suspense fallback={<></>}>
          <ErrorBoundary>
            <ActiveItemSidebarContent
              activeItem={activeItem}
              onSelect={onSelect}
            />
          </ErrorBoundary>
        </Suspense>
      )}
    </div>
  )
}
